export type ColumnMeta = {
  className?: string;
  widthMode?: 'fit' | 'percent';
  widthSize?: number;
  widthMinSize?: number;
  widthMaxSize?: number;
  badgeVariant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'dark';
  displayLabelOptions?: ReadonlyArray<{ value: string; label: string }>;
};

export function getDisplayLabel(value: unknown, meta?: ColumnMeta): string | undefined {
  const options = (
    meta as { displayLabelOptions?: ReadonlyArray<{ value: string; label: string }> }
  )?.displayLabelOptions;
  if (!options || value == null) return undefined;
  const raw = String(value).toLowerCase();
  const match = options.find(opt => String(opt.value).toLowerCase() === raw);
  return match?.label;
}

export function getBadgeVariant(
  value: unknown,
  meta?: ColumnMeta
): 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'dark' {
  if (meta?.badgeVariant) return meta.badgeVariant;

  const v = String(value ?? '').toLowerCase();

  // Check danger first so "inactive"/"suspended" are not matched by the "active" in success
  if (/(error|fail|failed|cancel|cancelled|rejected|danger|inactive|suspended)/.test(v)) return 'danger';
  if (/(success|active|paid|complete|completed|ok|enabled)/.test(v)) return 'success';
  if (/(pending|wait|waiting|review|hold|warning)/.test(v)) return 'warning';
  if (/(info|note|info)/.test(v)) return 'info';
  if (/(primary|main|default)/.test(v)) return 'primary';

  return 'secondary';
}

export function renderBadge(value: unknown, meta?: ColumnMeta): JSX.Element {
  const mapped = getDisplayLabel(value, meta);
  const label = mapped ?? (value == null || value === '' ? '—' : String(value));
  const variant = getBadgeVariant(value, meta);

  const base =
    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 shadow-sm text-white';

  let variantClasses: string;
  switch (variant) {
    case 'success':
      variantClasses = 'bg-emerald-500 ring-emerald-500/60 dark:bg-emerald-400';
      break;
    case 'danger':
      variantClasses = 'bg-red-500 ring-red-500/60 dark:bg-red-400';
      break;
    case 'warning':
      variantClasses = 'bg-amber-400 ring-amber-400/60 dark:bg-amber-300';
      break;
    case 'info':
      variantClasses = 'bg-sky-500 ring-sky-500/60 dark:bg-sky-400';
      break;
    case 'primary':
      variantClasses = 'bg-primary ring-primary/60 dark:bg-primary';
      break;
    case 'dark':
      variantClasses = 'bg-slate-800 ring-slate-900/60 dark:bg-slate-900';
      break;
    default:
      // secondary
      variantClasses = 'bg-slate-400 ring-slate-500/80 dark:bg-slate-600';
      break;
  }

  return <span className={`${base} ${variantClasses}`}>{label}</span>;
}
